
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Group } from '@/types/group';
import { convertToGroup } from '@/utils/groupUtils';

export const useGroups = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: groups = [],
    isLoading: loading,
    error
  } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select(`
          *,
          clients:client_id (id, name),
          responsible_user:responsible_user_id (id, name),
          user_groups (count)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar grupos:', error);
        throw error;
      }

      const convertedGroups = (data as any[]).map((group) => ({
        ...group,
        user_count: group.user_groups?.[0]?.count || 0,
        client: group.clients,
        responsible_user: group.responsible_user
      })).map(convertToGroup);
      
      return convertedGroups as Group[];
    },
  });

  const addGroup = useMutation({
    mutationFn: async (groupData: any) => {
      const { data, error } = await supabase
        .from('groups')
        .insert([groupData])
        .select(`
          *,
          clients:client_id (id, name),
          responsible_user:responsible_user_id (id, name),
          user_groups (user_id)
        `)
        .single();

      if (error) {
        console.error('Erro ao criar grupo:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({
        title: "Sucesso",
        description: "Grupo criado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao criar grupo:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar grupo. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { data: updatedData, error } = await supabase
        .from('groups')
        .update(data)
        .eq('id', id)
        .select(`
          *,
          clients:client_id (id, name),
          responsible_user:responsible_user_id (id, name),
          user_groups (user_id)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar grupo:', error);
        throw error;
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({
        title: "Sucesso",
        description: "Grupo atualizado com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao atualizar grupo:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar grupo. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar grupo:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({
        title: "Sucesso",
        description: "Grupo removido com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao deletar grupo:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover grupo. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const addUserToGroup = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      const { error } = await supabase
        .from('user_groups')
        .insert([{ group_id: groupId, user_id: userId }]);

      if (error) {
        console.error('Erro ao adicionar usuário ao grupo:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({
        title: "Sucesso",
        description: "Usuário adicionado ao grupo com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao adicionar usuário ao grupo:', error);
      toast({
        title: "Erro",
        description: "Erro ao adicionar usuário ao grupo. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const removeUserFromGroup = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      const { error } = await supabase
        .from('user_groups')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', userId);

      if (error) {
        console.error('Erro ao remover usuário do grupo:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({
        title: "Sucesso",
        description: "Usuário removido do grupo com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao remover usuário do grupo:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover usuário do grupo. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    groups,
    loading,
    error,
    addGroup,
    updateGroup,
    deleteGroup,
    addUserToGroup,
    removeUserFromGroup,
  };
};
